/**
 * S05 · Executive Dashboard — CAIO Variant (HERO)
 * Persona: Chief AI Officer — strategy, approvals, ROI.
 *
 * Wave 29 — Bundle-independent. All data flows through the aggregate
 * `/api/dashboard` REST endpoint via `DashboardDataProvider`. Each
 * panel reads `useDashboardData()` directly; the only thing this file
 * orchestrates is layout + loading/error gates + the live SyncedIndicator.
 */
import React from "react";
import {
  TrendingUp,
  ShieldAlert,
  ClipboardCheck,
  Activity,
} from "lucide-react";
import { DashboardDataProvider, useDashboardData } from "@/contexts/DashboardDataContext";
import { Flywheel } from "@/components/brand/Flywheel";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { fmtAud, KpiTile } from "./components/DashboardPrimitives";
import { SystemStatusPanel } from "./components/SystemStatusPanel";
import HomeHero from "./components/HomeHero";
import {
  PeriodSelector,
  FlywheelPanel,
  OpportunitiesPanel,
  DepartmentsPanel,
  TopBurnersPanel,
  ApprovalsPanel,
  ShadowAiPanel,
  CompliancePanel,
  IncidentsPanel,
  VendorsPanel,
  AuditTicker,
} from "./components/CaioPanels";

export default function CaioDashboard() {
  return (
    <DashboardDataProvider>
      <CaioDashboardInner />
    </DashboardDataProvider>
  );
}

function CaioDashboardInner() {
  const { tenant, kpis, shadowAgents, loading, error, lastSyncedAt, refetch } =
    useDashboardData();

  if (loading && !tenant) return <CaioSkeleton />;
  if (error) return <CaioError message={error.message} />;

  const shadowCount = (shadowAgents || []).length;

  return (
    <div
      className="relative px-6 lg:px-8 py-6 anim-fade-up"
      data-testid="caio-dashboard"
    >
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.05} />
      </div>

      {/* Header strip */}
      <div className="relative flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Home · CAIO view</p>
          <h1 className="text-display-md text-ink-primary mt-1">
            {tenant?.name}
          </h1>
          <p className="text-ink-secondary mt-1">
            {tenant?.industry} ·{" "}
            <SyncedIndicator
              lastSyncedAt={lastSyncedAt}
              loading={loading}
              onRefetch={refetch}
              testId="caio-dashboard-synced"
            />
          </p>
        </div>
        <PeriodSelector />
      </div>

      {/* Persona-aware hero + today's digest + live savings ticker */}
      <HomeHero />

      {/* Hero KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <KpiTile
          label="Governed agents"
          value={(kpis.governedAgents ?? 0).toString()}
          delta={`${shadowCount} ungoverned`}
          deltaTone={shadowCount === 0 ? "up" : "down"}
          hint={`of ${kpis.totalAgents ?? 0} total`}
          icon={ShieldAlert}
          tone="tralenex"
        />
        <KpiTile
          label="ROI ratio"
          value={`${(kpis.roiRatio ?? 0).toFixed(1)}×`}
          delta="+0.4× WoW"
          deltaTone="up"
          hint={`${fmtAud(kpis.monthlyValueAud || 0)} value / ${fmtAud(kpis.monthlySpendAud || 0)} spend`}
          icon={TrendingUp}
          tone="low"
        />
        <KpiTile
          label="Open high-severity"
          value={(kpis.openHighIncidents ?? 0).toString()}
          delta={`${kpis.violations ?? 0} policy violations · 30d`}
          deltaTone={(kpis.openHighIncidents ?? 0) === 0 ? "up" : "down"}
          icon={Activity}
          tone="risk"
        />
        <KpiTile
          label="Pending my approval"
          value={(kpis.pendingApprovals ?? 0).toString()}
          delta="SLA 02d 04h soonest"
          deltaTone="neutral"
          icon={ClipboardCheck}
          tone="gold"
        />
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 flex flex-col gap-4">
          <FlywheelPanel />
          <TopBurnersPanel />
          <OpportunitiesPanel />
          <DepartmentsPanel />
        </div>

        <div className="flex flex-col gap-4">
          <ApprovalsPanel />
          <ShadowAiPanel />
          <CompliancePanel />
          <IncidentsPanel />
          <VendorsPanel />
        </div>
      </div>

      <div className="mt-6">
        <AuditTicker />
      </div>

      <div className="mt-4">
        <SystemStatusPanel />
      </div>
    </div>
  );
}

function CaioSkeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="caio-dashboard-loading">
      <div className="h-8 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 h-[480px] rounded-md surface-card animate-pulse bg-inset/20" />
        <div className="h-[480px] rounded-md surface-card animate-pulse bg-inset/20" />
      </div>
    </div>
  );
}

function CaioError({ message }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="caio-dashboard-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Home · CAIO view</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load the dashboard</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
      </div>
    </div>
  );
}
